/**
 * ── WHICH IMPLEMENTATION OF THE INDIRECT TERM DOES THIS SESSION RUN? (BUG 1) ────────────────────
 *
 * `src/render/indirect.ts` holds the law: **a constant ambient and an irradiance volume are two
 * implementations of ONE responsibility.** Until this module existed, the world could describe how
 * to BAKE an irradiance volume (`lighting.gi` — origin, spacing, the authored volume nesting) but
 * it could not say **which of the two implementations carries its indirect term at runtime.** That
 * choice was made by a `?gi=` URL parameter inside `if (import.meta.env.DEV)` in `scene.ts`, so:
 *
 *   - opening `?world=sponza-debug-01` normally gave NO GI, over a 912 KB atlas sitting on disk
 *     next to a world.json that says `lighting.gi.enabled: true`; and
 *   - **a production build had no path to an atlas at all** — the whole block folded away.
 *
 * So the one decision the law exists to arbitrate was the one decision world data could not
 * express. This module is the fix: `world.lighting.indirect` names the implementation, and the URL
 * becomes a DEV OVERRIDE of that field rather than the only way to state it.
 *
 * ── WHY A PURE, DEPENDENCY-FREE MODULE ──────────────────────────────────────────────────────────
 *
 * The same reason `bakeState.ts` is one: `scene.ts` is EAGER and must import this STATICALLY —
 * the activation is resolved during scene construction, before any `await import` of the GI chunk
 * can have happened. No `three`, no zod, no `probeFile`. It is in `tests/purity.test.ts`'s
 * `PURE_FILES` for the usual GI reason: a wrong answer here does not fail anything, it silently
 * lights (or fails to light) a room and reports success either way.
 *
 * ── ONE OF A CATEGORY, AND THE CATEGORY ALREADY HAS A SHAPE ─────────────────────────────────────
 *
 * This is deliberately `resolveAxonSelection(search, dev)`'s shape (`src/net/axon/gate.ts`). That
 * function already answers the identical question for a different subsystem — *"a URL may pick the
 * non-default backend in dev; production is always the shipping one BY RULE"* — and it is already
 * the thing `checkBundle` and a headless truth-table test are built around. Copied, not reinvented.
 */

/**
 * The four things a session can be doing about GI.
 *
 * `'off'` is A ROW, NOT AN ABSENCE, and it is the value this module exists to add as much as
 * `'file'` is. Before world data could turn GI on, *"no `?gi=` param"* WAS the off arm of every
 * A/B in `tests/e2e/gi*.e2e.ts`. Once a world can switch itself on, absence stops meaning off and
 * the control arm of every measurement loses its name — so it gets one.
 */
export type GiMode =
  /** No fetch, no volume, no bake. The constant fill carries the indirect term. */
  | 'off'
  /** Load `worlds/<id>/gi/` — FILE OR NOTHING, NEVER BAKE. The shipping shape. */
  | 'file'
  /** DEV: load the file, and fall through to an in-browser bake if there isn't a usable one. */
  | 'probe'
  /** DEV: bake in-browser, ignoring any file on disk. The G0–G4 measurement path. */
  | 'bake';

/*
 * ⚠️ `'dense'` IS GONE (F3, spec S5), and its removal is deliberately NOT a silent one.
 *
 * It named the v1 `LightProbeGrid` lattice — a second light class, a second node class, a second
 * atlas format and a second bake, reachable only from this enum and routed to by nothing. It was
 * kept "reachable BY NAME" so the G0–G4 numbers stayed reproducible during the sparse changeover;
 * that window closed when `axon.probeVolume/3` bumped the format and every atlas on disk with it,
 * so no v1 comparison transfers any more regardless.
 *
 * An old `?gi=dense` URL now takes the UNKNOWN-VALUE arm: one warn naming the four live modes, and
 * the answer FALLS THROUGH to world data. That is the right failure — it says the mode is gone
 * rather than quietly resolving to `'bake'`, which would produce a real, plausible, different atlas
 * and let two runs differ while both self-reported correctly.
 */

/** The `?gi=` values that name a mode. Anything else is ignored rather than defaulted. */
export const GI_MODE_PARAMS = ['off', 'file', 'probe', 'bake'] as const;

/** What a world says carries its indirect term. Mirrors `WorldLightingSchema.indirect`. */
export type WorldIndirect = 'fill' | 'baked';

export interface GiActivation {
  mode: GiMode;
  /**
   * WHERE the decision came from. `'url'` is dev-only and always wins.
   *
   * It is not decoration: an `absent` fetch verdict is SILENT under a dev URL (most worlds have no
   * atlas and never will) but is worth one `console.info` when the WORLD declared it has one and
   * there isn't one. The caller cannot tell those apart without this field.
   */
  from: 'url' | 'world';
  /**
   * Set when a `?gi=` value was present but named no mode. The caller warns ONCE and the answer
   * falls through to world data.
   *
   * Same discipline as `?giQuality=prod` today: an unrecognised REQUEST must never silently
   * resolve to a different REAL mode, because two runs then differ while both self-report
   * correctly.
   */
  ignoredParam: string | null;
}

/** Is this string one of the four modes? Used to REFUSE an unknown `?gi=`, never to default. */
export function isGiModeParam(s: string | null | undefined): s is GiMode {
  return typeof s === 'string' && (GI_MODE_PARAMS as readonly string[]).includes(s);
}

/**
 * Resolve what this session does about GI.
 *
 * PRECEDENCE, highest first:
 *
 *   1. `?gi=off` — no fetch, no volume, constant fill. The named control arm (see `GiMode`).
 *   2. `?gi=file|probe|bake` — that mode, **regardless of `lighting.indirect`**. A world set to
 *      `'fill'` can still be baked and inspected; a world set to `'baked'` can still be forced
 *      through the in-browser bake.
 *   3. Unknown `?gi=<junk>` — `ignoredParam` is set and the answer FALLS THROUGH to world data.
 *   4. World data — `lighting.indirect === 'baked'` ⇒ `'file'`, else `'off'`.
 *   5. In a production build `dev` is the compile-time literal `false`, so 1–3 fold out entirely
 *      and Rollup deletes the URL branch. **Production always resolves from world data.**
 *
 * ⭐ WORLD DATA RESOLVES TO `'file'`, NEVER TO `'probe'`, and the distinction is not cosmetic.
 * `'probe'` falls through to an in-browser bake, which is one long synchronous JS turn — minutes
 * of frozen tab on a player's machine, hiding a broken build behind a hang. And it would make the
 * bake knobs live: `?giIntensity=2` on a world set to `'baked'` would silently start a multi-minute
 * bake where the artist expected a gain knob on the loaded file. File or nothing.
 */
export function resolveGiActivation(
  search: string,
  worldIndirect: WorldIndirect,
  dev: boolean,
): GiActivation {
  const fromWorld: GiActivation = {
    mode: worldIndirect === 'baked' ? 'file' : 'off',
    from: 'world',
    ignoredParam: null,
  };
  // Rule 5. `dev` is a compile-time literal at the one production call site, so everything below
  // this line is dead code in `dist/` and the URL cannot reach a shipped build.
  if (!dev) return fromWorld;
  const raw = new URLSearchParams(search).get('gi');
  if (raw === null || raw === '') return fromWorld;
  if (isGiModeParam(raw)) return { mode: raw, from: 'url', ignoredParam: null };
  return { ...fromWorld, ignoredParam: raw };
}
